import { useCallback, useEffect, useRef, useState } from 'react';

import { HCAPTCHA_SITE_KEY } from '../../helpers/settings';

type HCaptchaWidgetId = string | number;

interface HCaptchaApi {
  execute: (widgetId: HCaptchaWidgetId) => void;
  render: (
    container: string | HTMLElement,
    params: Record<string, any>,
  ) => HCaptchaWidgetId;
  reset: (widgetId: HCaptchaWidgetId) => void;
}

interface PendingCaptcha {
  reject: (error: Error) => void;
  resolve: (token: string) => void;
}

declare global {
  interface Window {
    hcaptcha?: HCaptchaApi;
  }
}

const HCAPTCHA_SCRIPT_SRC = 'https://js.hcaptcha.com/1/api.js?render=explicit';
const HCAPTCHA_CONTAINER_ID = 'hcaptcha-invisible-container';

const getOrCreateContainer = (): HTMLElement => {
  let container = document.getElementById(HCAPTCHA_CONTAINER_ID);
  if (container) {
    return container;
  }

  container = document.createElement('div');
  container.id = HCAPTCHA_CONTAINER_ID;
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.width = '1px';
  container.style.height = '1px';
  document.body.appendChild(container);
  return container;
};

export const useHCaptcha = () => {
  const [loaded, setLoaded] = useState(false);
  const widgetIdRef = useRef<HCaptchaWidgetId | null>(null);
  const pendingCaptchaRef = useRef<PendingCaptcha | null>(null);

  useEffect(() => {
    if (!HCAPTCHA_SITE_KEY) {
      return;
    }

    if (window.hcaptcha) {
      setLoaded(true);
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${HCAPTCHA_SCRIPT_SRC}"]`,
    ) as HTMLScriptElement | null;

    const onLoad = () => setLoaded(true);
    const onError = () => {
      setLoaded(false);
      console.error('[hCaptcha] Failed to load script');
    };

    if (existingScript) {
      existingScript.addEventListener('load', onLoad);
      existingScript.addEventListener('error', onError);
      return () => {
        existingScript.removeEventListener('load', onLoad);
        existingScript.removeEventListener('error', onError);
      };
    }

    const script = document.createElement('script');
    script.src = HCAPTCHA_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
  }, []);

  const ensureWidget = useCallback(() => {
    if (!window.hcaptcha) {
      throw new Error('hCaptcha is not ready');
    }

    if (widgetIdRef.current !== null) {
      return widgetIdRef.current;
    }

    const container = getOrCreateContainer();
    widgetIdRef.current = window.hcaptcha.render(container, {
      sitekey: HCAPTCHA_SITE_KEY,
      size: 'invisible',
      callback: (token: string) => {
        pendingCaptchaRef.current?.resolve(token);
        pendingCaptchaRef.current = null;
        if (widgetIdRef.current !== null) {
          window.hcaptcha?.reset(widgetIdRef.current);
        }
      },
      'error-callback': () => {
        pendingCaptchaRef.current?.reject(new Error('hCaptcha verification failed'));
        pendingCaptchaRef.current = null;
      },
      'expired-callback': () => {
        pendingCaptchaRef.current?.reject(new Error('hCaptcha token expired'));
        pendingCaptchaRef.current = null;
      },
    });

    return widgetIdRef.current;
  }, []);

  const execute = useCallback((): Promise<string> => {
    if (!HCAPTCHA_SITE_KEY) {
      return Promise.reject(new Error('hCaptcha sitekey is not configured'));
    }

    if (!loaded || !window.hcaptcha) {
      return Promise.reject(new Error('hCaptcha is not loaded yet'));
    }

    if (pendingCaptchaRef.current) {
      return Promise.reject(new Error('hCaptcha verification already in progress'));
    }

    const widgetId = ensureWidget();
    return new Promise<string>((resolve, reject) => {
      pendingCaptchaRef.current = { resolve, reject };
      try {
        window.hcaptcha?.execute(widgetId);
      } catch (error) {
        pendingCaptchaRef.current = null;
        reject(error instanceof Error ? error : new Error('Failed to execute hCaptcha'));
      }
    });
  }, [ensureWidget, loaded]);

  return { execute, loaded };
};
